/**
 * Feature E02-F02 — "Детальная карточка проекта + степпер" pure model (T03/T04/T05/T06).
 *
 * Framework-free, DOM-free decision logic shared by the React view
 * (`ProjectDetailView.tsx`, `ProjectDetailHeader.tsx`, `StageStepper.tsx`,
 * `ClientAutocomplete.tsx`) and the unit tests — so no rule lives in the JSX.
 *
 * 1:1 port of the legacy front-end card logic (`../EER app/public/assets/js/
 * project.js`):
 *   - `updateStepper`            → the 4-step pipeline + the stage-5→4 collapse
 *     (`mapStage: s === 5 ? 4 : s`); the DB stage is 1-based, the stepper index
 *     is 0-based.
 *   - `setProjectStageView`      → the preview banner ("Viewing stage X — Back to
 *     current") shown while previewing a non-current stage WITHOUT mutating the
 *     real `project.stage`.
 *   - `renderProjectHeader`      → the green "active" status badge
 *     (`status === 'active'`) and the empty-role-name alert guard.
 *   - `renderClientAutocomplete` → the client dropdown: substring filter, the
 *     first-10-when-empty cap, the "Add new: …" row (→ client_id = null), and the
 *     critical RENAME edge — typing a name that no longer matches the linked
 *     client clears `client_id` to null so a NEW client is created on save.
 *   - `saveContactEdit`          → assembling first+last into a single contact
 *     name (the api fills 'Unknown' when both are blank; T06 covers the create/
 *     link itself server-side).
 */
import type { DetailProject, ContactEdit } from '@eer/shared';

// Client-autocomplete rules moved to the feature-neutral shared model (they now
// back BOTH the project header and the E06 Research Intake form). Re-exported here
// so this module's existing importers (ClientAutocomplete, ProjectDetailHeader,
// projectDetailModel.test) keep their `./projectDetailModel.js` import unchanged.
export {
  CLIENT_AUTOCOMPLETE_EMPTY_LIMIT,
  filterClientOptions,
  shouldOfferAddNew,
  addNewLabel,
  resolveClientName,
} from '../shared/clientAutocompleteModel.js';
export type { ClientResolution } from '../shared/clientAutocompleteModel.js';

// ---------------------------------------------------------------------------
// Stage stepper (T04) — the 4-step pipeline + stage-5→4 collapse
// ---------------------------------------------------------------------------

/**
 * The four pipeline stages drawn by the stepper (legacy `updateStepper` labels).
 * The DB stage column carries 1..5, where 5 ("Launched") COLLAPSES into step 4
 * (the post-launch state shares the last marker). The stepper is ALWAYS 4 steps.
 */
export const STAGE_LABELS = ['Pre-Kickoff', 'Kick-off', 'Generation', 'Launched'] as const;

/** Number of steps the stepper always renders (legacy: exactly four). */
export const STAGE_STEP_COUNT = STAGE_LABELS.length;

/**
 * Map a 1-based DB stage (1..5, nullable) to the 0-based stepper index (0..3).
 * Stage 5 collapses to index 3 (`s === 5 ? 4 : s`, then −1 to 0-base). A
 * missing/invalid stage clamps to the first step (legacy default for a fresh
 * project with no stage yet).
 */
export function stageToStepIndex(stage: number | null | undefined): number {
  if (stage === null || stage === undefined || !Number.isFinite(stage)) return 0;
  const collapsed = stage === 5 ? 4 : stage; // post-launch shares step 4
  const oneBased = Math.min(Math.max(Math.trunc(collapsed), 1), STAGE_STEP_COUNT);
  return oneBased - 1;
}

/** Inverse of {@link stageToStepIndex}: a 0-based step index → its display number. */
export function stepNumber(index: number): number {
  return index + 1;
}

// ---------------------------------------------------------------------------
// Stage-preview banner (T04) — "Viewing stage X" / "Back to current"
// ---------------------------------------------------------------------------

/** Caption shown in the preview banner while viewing a non-current stage. */
export const BACK_TO_CURRENT_LABEL = 'Back to current';

/**
 * Resolve the NAME of a 0-based step index for the preview banner (legacy
 * `stageNames` map: `{1:'Pre-Kickoff',2:'Kick-off',3:'Generation',4:'Launched'}`).
 * An out-of-range index falls back to its 1-based number (legacy `|| p.stage`).
 */
export function stageName(index: number): string {
  return STAGE_LABELS[index] ?? String(stepNumber(index));
}

/**
 * Resolve the preview-banner state for a (current, previewed) pair. The banner is
 * only shown when the user is previewing a DIFFERENT stage than the current one
 * (legacy `setProjectStageView`: previewing the current stage is a no-op).
 *
 * @returns `{ visible, label, currentLabel }` — when visible, `label` names the
 *   PREVIEWED stage ("Viewing stage Kick-off") and `currentLabel` names the real
 *   current stage ("Current project stage: Pre-Kickoff"), matching the legacy copy
 *   (`renderStageContent` banner). `label` is the previewed-stage name alone so the
 *   view can bold it inline.
 */
export function previewBanner(
  currentIndex: number,
  previewIndex: number | null | undefined,
): { readonly visible: boolean; readonly label: string; readonly currentLabel: string } {
  if (previewIndex === null || previewIndex === undefined || previewIndex === currentIndex) {
    return { visible: false, label: '', currentLabel: '' };
  }
  return {
    visible: true,
    label: stageName(previewIndex),
    currentLabel: stageName(currentIndex),
  };
}

// ---------------------------------------------------------------------------
// Status badge (T03) — green when status === 'active'
// ---------------------------------------------------------------------------

/** The descriptor the header uses to paint the status badge. */
export interface StatusBadge {
  /** Raw display text (the raw status, or 'New' when null/empty — legacy default). */
  readonly label: string;
  /**
   * UPPERCASED display text for the pill (legacy `String(status).toUpperCase()`,
   * ~project.js L672). The header renders THIS; `label` stays raw for callers that
   * need the un-cased value.
   */
  readonly displayLabel: string;
  /** Whether this is the green "active" badge (legacy `status === 'active'`). */
  readonly isActive: boolean;
  /** Tailwind classes for the pill (green for active, gray otherwise). */
  readonly className: string;
}

/** Build the status badge descriptor for a project status (T03). */
export function statusBadge(status: string | null | undefined): StatusBadge {
  const raw = (status ?? '').trim();
  const isActive = raw.toLowerCase() === 'active';
  const label = raw.length > 0 ? raw : 'New';
  return {
    label,
    displayLabel: label.toUpperCase(),
    isActive,
    className: isActive
      ? 'bg-green-100 text-green-800 border border-green-200'
      : 'bg-gray-100 text-gray-600 border border-gray-200',
  };
}

// ---------------------------------------------------------------------------
// AI-analysis status badge (E03-F02-T06) — analyzed vs not-analyzed
// ---------------------------------------------------------------------------

/** The descriptor the header uses to paint the "analyzed"/"not analyzed" pill. */
export interface AnalysisStatusBadge {
  /** True once a Pre-Kickoff AI analysis has ever completed for the project. */
  readonly analyzed: boolean;
  readonly label: string;
  /** Tailwind classes for the pill (green once analyzed, gray beforehand). */
  readonly className: string;
}

/**
 * Build the AI-analysis status badge from `project.last_analysis_at`
 * (E03-F02-T06 backend field — set once the FIRST `ai_analysis_completed`
 * event lands, never cleared afterwards). A present-but-empty string is
 * treated as "no analysis yet" defensively (parity with the other header
 * fields' blank-string guards).
 */
export function analysisStatusBadge(
  lastAnalysisAt: string | null | undefined,
): AnalysisStatusBadge {
  const analyzed = (lastAnalysisAt ?? '').trim().length > 0;
  return {
    analyzed,
    label: analyzed ? 'Analyzed' : 'Not analyzed',
    className: analyzed
      ? 'bg-green-100 text-green-800 border border-green-200'
      : 'bg-gray-100 text-gray-600 border border-gray-200',
  };
}

// ---------------------------------------------------------------------------
// Role-name guard (T03) — block saving an empty role name
// ---------------------------------------------------------------------------

/** Alert text shown when the user tries to save an empty role name (legacy). */
export const EMPTY_ROLE_NAME_ALERT = 'Role name cannot be empty';

/** True when a role-name edit is valid to persist (non-blank after trim). */
export function isRoleNameValid(value: string | null | undefined): boolean {
  return (value ?? '').trim().length > 0;
}

// ---------------------------------------------------------------------------
// Missing-field highlight (PK-QA-1 UX) — point at the header field the
// Pre-Kickoff 400 VALIDATION_ERROR reported as missing
// ---------------------------------------------------------------------------

/**
 * The header form-field keys the Pre-Kickoff analysis 400 `VALIDATION_ERROR` can
 * report as missing (`client_name` / `role_name` / `contact_email`). Any other key
 * in the reported set is ignored by the header highlight — these three are the only
 * ones with an editable control in the header.
 */
export const MISSING_HIGHLIGHT_KEYS = ['client_name', 'role_name', 'contact_email'] as const;

/** The header form key the Pre-Kickoff analysis uses for the contact email field. */
export const CONTACT_EMAIL_FIELD_KEY = 'contact_email';

/**
 * Tailwind classes for the thin red outline drawn on a still-missing required
 * field. A ring (box-shadow) rather than a `border` so it never fights the field's
 * own border utilities (the client/role inputs are underline-style, the contact
 * input is fully bordered); `red-400` matches the existing role-error shade
 * (`EMPTY_ROLE_NAME_ALERT` / `project-detail-role-error`).
 */
export const MISSING_FIELD_HIGHLIGHT_CLASS = 'rounded ring-2 ring-red-400';

/**
 * Whether a header field should show the missing-required highlight: it is in the
 * reported `missing` set AND still blank. Gating on "still blank" means the outline
 * clears the instant the user fills the field in — natural per-field UX with no
 * extra callback, since typing re-renders the header with the new value.
 */
export function shouldHighlightMissing(
  key: string,
  value: string | null | undefined,
  missing: ReadonlySet<string> | null | undefined,
): boolean {
  return !!missing && missing.has(key) && (value ?? '').trim().length === 0;
}

// ---------------------------------------------------------------------------
// Contact name assembly (T06 web side) — first+last → one name
// ---------------------------------------------------------------------------

/** Fallback contact name when both first and last are blank (api parity). */
export const CONTACT_FALLBACK_NAME = 'Unknown';

/**
 * Assemble a single display name from a contact edit triple (legacy
 * `saveContactEdit`: `${first} ${last}`.trim()). Blank both → '' (the api fills
 * 'Unknown'); this returns the trimmed join for the optimistic UI label.
 */
export function assembleContactName(
  contact: Pick<ContactEdit, 'first_name' | 'last_name'>,
): string {
  return `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim();
}

/**
 * Split a stored single contact name back into a `{first_name,last_name}` pair
 * for the inline contact editor (the first token is the first name, the rest is
 * the last name — parity with the legacy `name.split(' ')`).
 */
export function splitContactName(name: string | null | undefined): {
  first_name: string;
  last_name: string;
} {
  const tokens = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return { first_name: '', last_name: '' };
  const [first, ...rest] = tokens;
  return { first_name: first ?? '', last_name: rest.join(' ') };
}

// ---------------------------------------------------------------------------
// Header projection (T03) — pull the header's display fields off the project
// ---------------------------------------------------------------------------

/** The fields the 3-column header renders for a project. */
export interface HeaderFields {
  readonly roleName: string;
  readonly clientName: string;
  readonly contactName: string;
  readonly contactEmail: string;
  readonly recruiter: string;
  readonly status: StatusBadge;
  /** E03-F02-T06 — "Analyzed"/"Not analyzed" pill (Pre-Kickoff AI analysis). */
  readonly analysisStatus: AnalysisStatusBadge;
}

/** Project the header display fields (with sensible empty-state placeholders). */
export function headerFields(project: DetailProject): HeaderFields {
  return {
    roleName: (project.role_name ?? '').trim() || 'New Position',
    clientName: (project.client_name ?? '').trim() || '—',
    contactName: (project.contact_name ?? '').trim() || '—',
    contactEmail: (project.contact_email ?? '').trim(),
    recruiter: (project.owner_id ?? '').trim() || '—',
    status: statusBadge(project.status),
    analysisStatus: analysisStatusBadge(project.last_analysis_at),
  };
}
